import { AlertCircle, RefreshCw, ShieldCheck, UserRound, UserRoundCheck } from "lucide-react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useBatchErrorMessage } from "@/modules/dashboard/payout-batches/hooks/useBatchErrorMessage";
import { usePayoutBatchApprovalPolicy } from "@/modules/dashboard/payout-batches/hooks/usePayoutBatchApprovalPolicy";
import { classifyBatchError } from "@/modules/dashboard/payout-batches/utils/batchErrors";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaLineSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import type { SettingsTranslations } from "@/shared/lang/interfaces/settings";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { colocaToast } from "@/shared/utils/toast";

import { PAYOUT_BATCH_APPROVAL_SECTION_ID } from "../constants/payoutBatchApprovalSection";
import { type PayoutBatchApprovalMode, payoutBatchApprovalMode } from "../utils/payoutBatchApprovalMode";
import { SettingsSection } from "./SettingsSection";

const MODE_COPY: Record<
  PayoutBatchApprovalMode,
  { title: keyof SettingsTranslations; body: keyof SettingsTranslations }
> = {
  dual: { title: "payout_batch_approval_mode_dual_title", body: "payout_batch_approval_mode_dual_body" },
  sole_approver: {
    title: "payout_batch_approval_mode_sole_title",
    body: "payout_batch_approval_mode_sole_body",
  },
  self_setting: {
    title: "payout_batch_approval_mode_self_title",
    body: "payout_batch_approval_mode_self_body",
  },
  no_approver: {
    title: "payout_batch_approval_mode_none_title",
    body: "payout_batch_approval_mode_none_body",
  },
};

/**
 * Who approves a batch before its payments go out, and the organization's switch to let whoever prepares one
 * approve it. Only approvers flip it, each time behind their code; the backend audits it and tells Coloca. A
 * one-person organization does not need it: its only approver already approves their own batches.
 */
export function PayoutBatchApprovalSection() {
  const { t } = useTranslations("settings");
  const { user, can } = useAuth();
  const describeError = useBatchErrorMessage();
  const canChange = can("payout_batches:approve:write");
  const { policy, isLoading, hasError, reload, setSelfApproval, twoFactor } = usePayoutBatchApprovalPolicy(
    user?.organization_id ?? null,
  );
  const enabled = policy?.self_approval_enabled ?? false;
  // The prompt names what is about to change, so the code is the confirmation.
  const turningOn = !enabled;

  const toggle = async (next: boolean) => {
    try {
      const updated = await setSelfApproval(next);
      if (!updated) return;
      colocaToast.success(
        t(
          updated.self_approval_enabled
            ? "payout_batch_approval_enabled_toast"
            : "payout_batch_approval_disabled_toast",
        ),
      );
    } catch (error: unknown) {
      colocaToast.error(describeError(classifyBatchError(error)));
    }
  };

  return (
    <SettingsSection
      id={PAYOUT_BATCH_APPROVAL_SECTION_ID}
      eyebrow={t("payout_batch_approval_section_eyebrow")}
      title={t("payout_batch_approval_section_title")}
      sub={t("payout_batch_approval_section_sub")}
    >
      {isLoading && !policy ? (
        <ColocaLineSkeleton lines={3} />
      ) : !policy ? (
        hasError && (
          <ColocaAlert
            variant="destructive"
            icon={AlertCircle}
            text={
              <span className="flex flex-wrap items-center gap-3">
                {t("payout_batch_approval_load_error")}
                <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={reload}>
                  {t("payout_batch_approval_retry")}
                </ColocaButton>
              </span>
            }
          />
        )
      ) : (
        <>
          <ModeCard mode={payoutBatchApprovalMode(policy, canChange)} />
          <div className="mt-3 flex items-start justify-between gap-4 rounded-md border border-border p-3.5">
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-foreground">
                {t("payout_batch_approval_switch_label")}
              </p>
              <p className="mt-1 text-[12.5px] text-muted-foreground">
                {t("payout_batch_approval_switch_description")}
              </p>
            </div>
            <ColocaSwitch
              checked={enabled}
              disabled={!canChange || twoFactor.isRunning}
              onCheckedChange={(next) => void toggle(next)}
              aria-label={t("payout_batch_approval_switch_aria")}
            />
          </div>
          <p className="mt-2.5 text-xs text-muted-foreground">
            {canChange ? t("payout_batch_approval_change_note") : t("payout_batch_approval_read_only_note")}
          </p>
        </>
      )}
      <TwoFactorActionModals
        controller={twoFactor}
        requiredDescription={t("payout_batch_approval_two_factor_required")}
        challengeTitle={
          turningOn ? t("payout_batch_approval_enable_title") : t("payout_batch_approval_disable_title")
        }
        challengeDescription={
          turningOn
            ? t("payout_batch_approval_enable_description")
            : t("payout_batch_approval_disable_description")
        }
        challengePinDescription={
          turningOn
            ? t("payout_batch_approval_enable_description")
            : t("payout_batch_approval_disable_description")
        }
      />
    </SettingsSection>
  );
}

/** The mode in one sentence, drawn with the guide's own figures: two people, or one with their code. */
function ModeCard({ mode }: { mode: PayoutBatchApprovalMode }) {
  const { t } = useTranslations("settings");
  const copy = MODE_COPY[mode];
  const selfApproves = mode === "sole_approver" || mode === "self_setting";

  return (
    <div
      className={cn(
        "flex items-start gap-3.5 rounded-md border p-3.5",
        selfApproves ? "border-primary/30 bg-primary/5" : "border-border bg-card",
      )}
    >
      <div aria-hidden="true" className="flex shrink-0 items-center pt-0.5">
        {selfApproves ? (
          <span className="relative flex size-9 items-center justify-center rounded-full border border-primary/40 bg-primary/10">
            <UserRoundCheck className="size-4 text-primary dark:text-foreground" />
            <span className="absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-card">
              <ShieldCheck className="size-2.5" />
            </span>
          </span>
        ) : (
          <>
            <span className="flex size-9 items-center justify-center rounded-full border border-border bg-background">
              <UserRound className="size-4 text-muted-foreground" />
            </span>
            <span className="h-px w-3 bg-border" />
            <span className="flex size-9 items-center justify-center rounded-full border border-primary/40 bg-primary/10">
              <UserRoundCheck className="size-4 text-primary dark:text-foreground" />
            </span>
          </>
        )}
      </div>
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-foreground">{t(copy.title)}</p>
        <p className="mt-1 text-[12.5px] text-muted-foreground">{t(copy.body)}</p>
      </div>
    </div>
  );
}
